import {createRecipeLibraryEntry,discoverRecipes} from './library.js';
import {recipeDetailModel} from './detail.js';
import {recipeSaveSourceState} from './recipe-save.js';
import {renderRecipePage} from './page-view.js';
import {createRecipeAnalytics} from './analytics.js';

function filters(value={}){
  return Object.freeze({
    query:String(value.query??'').trim(),
    productCategory:String(value.productCategory??'').trim().toLowerCase(),
    stageType:String(value.stageType??'').trim().toLowerCase(),
    compatibility:String(value.compatibility??'').trim().toLowerCase()
  });
}

export function createRecipePageController({
  loadEntries,
  loadSaved=async()=>[],
  userId,
  locale='pl',
  smokerCapabilities=null,
  onUseRecipe=null,
  onSaveRecipe=null,
  onCopyRecipe=null,
  analytics=null,
  isOnline=()=>globalThis.navigator?.onLine!==false
}={}){
  if(typeof loadEntries!=='function')throw new TypeError('loadEntries is required');
  if(typeof loadSaved!=='function')throw new TypeError('loadSaved must be a function');
  const owner=String(userId??'').trim();
  if(!owner)throw new TypeError('userId is required');
  const recipeAnalytics=createRecipeAnalytics({analytics});

  let raw=[];
  let saved=[];
  let usePromise=null;
  let state=Object.freeze({
    kind:'loading',
    scope:'public',
    filters:filters(),
    entries:Object.freeze([]),
    notice:'',
    noticeKind:'neutral'
  });

  function savedByRecipe(){
    return new Map((saved||[]).filter(Boolean).map(item=>[String(item.recipeId),item]));
  }

  function capabilities(){
    return Object.freeze({
      save:typeof onSaveRecipe==='function',
      copy:typeof onCopyRecipe==='function'
    });
  }

  function entryFromRecord(record){
    const guidance=record?.guidance&&typeof record.guidance==='object'?record.guidance:{};
    return createRecipeLibraryEntry(record.plan,{
      locale,
      smokerCapabilities,
      ...guidance
    });
  }

  function listState({scope=state.scope,filterValues=state.filters,notice='',noticeKind='neutral'}={}){
    const all=raw.map(entryFromRecord);
    const nextFilters=filters(filterValues);
    const saves=savedByRecipe();
    return Object.freeze({
      kind:'list',
      scope,
      filters:nextFilters,
      entries:discoverRecipes(all,{
        scope,
        userId:owner,
        savedRecipeIds:[...saves.keys()],
        ...nextFilters
      }),
      savedRecipeIds:Object.freeze([...saves.keys()]),
      notice,
      noticeKind
    });
  }

  async function loadAll(){
    const [entries,saves]=await Promise.all([loadEntries(),loadSaved()]);
    raw=[...(entries||[])];
    saved=[...(saves||[])];
  }

  async function refresh(){
    state=Object.freeze({...state,kind:'loading'});
    try{
      await loadAll();
      state=listState();
      recipeAnalytics.listOpened({scope:state.scope,offline:!isOnline()});
    }catch{
      state=Object.freeze({...state,kind:'error'});
    }
    return state;
  }

  function setScope(scope){
    const next=['mine','saved'].includes(scope)?scope:'public';
    state=listState({scope:next});
    recipeAnalytics.listOpened({scope:next,offline:!isOnline()});
    return state;
  }

  function setFilters(values){
    state=listState({filterValues:values});
    return state;
  }

  function open(id,{notice='',noticeKind='neutral'}={}){
    const record=raw.find(item=>item?.plan?.recipe?.id===String(id));
    if(!record)throw new Error('RECIPE_NOT_FOUND');
    const guidance=record.guidance&&typeof record.guidance==='object'?record.guidance:{};
    const detail=recipeDetailModel(record.plan,{
      locale,
      smokerCapabilities,
      ...guidance
    });
    if(detail.source.type==='private'&&record.plan.recipe.authorUserId!==owner){
      throw new Error('RECIPE_NOT_ACCESSIBLE');
    }
    const save=savedByRecipe().get(String(id))||null;
    state=Object.freeze({
      kind:'detail',
      scope:state.scope,
      filters:state.filters,
      detail,
      record,
      saved:Boolean(save),
      save,
      savedSourceState:save?recipeSaveSourceState(save,record.plan.recipe):null,
      capabilities:capabilities(),
      notice,
      noticeKind
    });
    recipeAnalytics.viewed({
      recipe:record.plan.recipe,
      compatibility:detail.compatibility,
      offline:!isOnline()
    });
    recipeAnalytics.compatibilityWarningShown({
      recipe:record.plan.recipe,
      compatibility:detail.compatibility,
      offline:!isOnline()
    });
    return state;
  }

  function back(){
    state=listState();
    return state;
  }

  async function useSelected(){
    if(state.kind!=='detail')throw new Error('RECIPE_DETAIL_REQUIRED');
    if(typeof onUseRecipe!=='function')throw new Error('RECIPE_USE_UNAVAILABLE');
    if(usePromise)return usePromise;
    const record=state.record;
    const compatibility=state.detail?.compatibility||'unknown';
    recipeAnalytics.useStarted({
      recipe:record.plan.recipe,
      compatibility,
      offline:!isOnline()
    });
    usePromise=(async()=>{
      try{
        const result=await onUseRecipe(record);
        recipeAnalytics.draftCreated({
          recipe:record.plan.recipe,
          offline:!isOnline()
        });
        state=Object.freeze({...state,notice:'Utworzono edytowalny szkic w Dzienniku.',noticeKind:'success'});
        return result;
      }catch(error){
        state=Object.freeze({...state,notice:'Nie udało się utworzyć szkicu z receptury.',noticeKind:'error'});
        throw error;
      }finally{
        usePromise=null;
      }
    })();
    return usePromise;
  }

  async function secondary(action){
    if(state.kind!=='detail')throw new Error('RECIPE_DETAIL_REQUIRED');
    const record=state.record;
    const currentId=record.plan.recipe.id;
    if(action==='save'){
      if(typeof onSaveRecipe!=='function')return false;
      const result=await onSaveRecipe(record,{saved:state.saved});
      if(result?.saved===false)recipeAnalytics.unsaved({recipe:record.plan.recipe,offline:!isOnline()});
      else recipeAnalytics.saved({recipe:record.plan.recipe,offline:!isOnline()});
      saved=[...(await loadSaved())];
      open(currentId,{
        notice:result?.saved===false?'Usunięto z zapisanych.':'Zapisano recepturę.',
        noticeKind:'success'
      });
      return result;
    }
    if(action==='copy'){
      if(typeof onCopyRecipe!=='function')return false;
      const result=await onCopyRecipe(record);
      await loadAll();
      if(result?.plan?.recipe?.id){
        open(result.plan.recipe.id,{notice:'Utworzono prywatną kopię receptury.',noticeKind:'success'});
      }else{
        state=listState({scope:'mine',notice:'Utworzono prywatną kopię receptury.',noticeKind:'success'});
      }
      return result;
    }
    return false;
  }

  function getState(){return state;}
  function render(){return renderRecipePage(state);}

  return Object.freeze({
    refresh,setScope,setFilters,open,back,useSelected,secondary,getState,render
  });
}
